import { ApolloError } from '@apollo/client';
import dayjs from 'dayjs';

import { describeError, getRateLimitRejection } from '../../../common/rateLimit';
import { people } from '../tagging/tagging';

/**
 * What the tag manager and the list manager share: reading the API's refusals,
 * and the dates they show. Pure, so it is tested without rendering.
 *
 * The operations are CRM_TAG_AND_LIST_MANAGER.md's in ilbp-backend: card 17.5's
 * rename and delete for tags and lists, taken ahead of the rest of that card.
 */

export type ManagedNoun = 'tag' | 'list';

/** The fields an editor dialog can put a refusal on. */
export type ManagedField = 'name' | 'category' | 'color' | 'description';

const FIELDS: ReadonlyArray<string> = ['name', 'category', 'color', 'description'];

const firstError = (error: unknown) => (error instanceof ApolloError ? error.graphQLErrors?.[0] : undefined);

export const manageErrorCode = (error: unknown) => firstError(error)?.extensions?.code as string | undefined;

/** The field a refusal is about, so the editor can show it under that field rather than above the form. */
export const fieldOfError = (error: unknown): ManagedField | undefined => {
  if (manageErrorCode(error) === 'CRM_TAG_NAME_TAKEN') return 'name';
  const field = firstError(error)?.extensions?.field;
  return typeof field === 'string' && FIELDS.includes(field) ? (field as ManagedField) : undefined;
};

/**
 * How many people carry a tag now, when its delete was refused because that
 * was not the number confirmed. Nothing was deleted; confirm this one.
 */
export const countNow = (error: unknown): number | undefined => {
  if (manageErrorCode(error) !== 'CRM_SELECTION_CHANGED') return undefined;
  const count = firstError(error)?.extensions?.count;
  return typeof count === 'number' ? count : undefined;
};

/**
 * An API without these operations refuses the request as a whole: the field is
 * not in its schema. The admin was deployed before the API.
 */
export const isUnsupported = (error: unknown) =>
  manageErrorCode(error) === 'GRAPHQL_VALIDATION_FAILED' || /Cannot query field/.test(firstError(error)?.message ?? '');

const ids = (value: unknown): Array<number> => (Array.isArray(value) ? value.filter((v) => typeof v === 'number') : []);

const numbered = (word: string, list: Array<number>) =>
  list.length ? `${word}${list.length === 1 ? '' : 's'} ${list.join(', ')}` : '';

/** What uses a tag or list that could not be deleted: "campaign 74 and workflow 12". */
export const describeUsers = (error: unknown): string => {
  const ext = firstError(error)?.extensions ?? {};
  return [numbered('campaign', ids(ext.campaignIds)), numbered('workflow', ids(ext.workflowIds))].filter(Boolean).join(' and ');
};

/** What to tell the admin when a change is refused. Nothing was changed in any of these. */
export const describeManageError = (error: unknown, noun: ManagedNoun): string => {
  if (getRateLimitRejection(error)) return describeError(error, 'Too many changes');
  if (isUnsupported(error)) {
    return 'The API this dashboard uses cannot do this yet. Deploy the API with the tag and list manager first.';
  }

  const first = firstError(error);
  switch (manageErrorCode(error)) {
    case 'CRM_TAG_NAME_TAKEN':
      return 'There is already a tag with that name, in some letter case. Pick another name, or use that tag.';
    case 'CRM_TAG_SYSTEM':
      return 'This is a system tag, set by automation: it keeps its name and cannot be deleted. Its colour, category and description can change.';
    case 'CRM_TAG_NOT_FOUND':
      return 'That tag no longer exists. Someone may have just deleted it.';
    case 'CRM_LIST_NOT_FOUND':
      return 'That list no longer exists. Someone may have just deleted it.';
    case 'CRM_SELECTION_CHANGED': {
      const count = countNow(error);
      if (noun === 'list') {
        return count === undefined
          ? 'The filter matches a different number of people now. Nothing was made.'
          : `The filter matches ${people(count)} now, not the number you confirmed. Nothing was made. Check the new number and confirm again.`;
      }
      return count === undefined
        ? 'The number of people carrying this tag changed. Nothing was deleted.'
        : `${people(count)} carry this tag now, not the number you confirmed. Nothing was deleted. Check the new number and confirm again.`;
    }
    case 'CRM_IN_USE': {
      const users = describeUsers(error);
      return noun === 'tag'
        ? `A workflow that has not ended triggers on this tag or sets it${users ? ` (${users})` : ''}. End or change it first. Nothing was deleted.`
        : `Something still to run uses this list${users ? ` (${users})` : ''}: a campaign still to send or a workflow still running. End or change it first. Nothing was deleted.`;
    }
    case 'CRM_INVALID_QUERY':
      return first?.message ?? 'The API refused this change.';
    case 'CRM_UNAVAILABLE':
      return 'The CRM database is not available on this server.';
    case 'FORBIDDEN':
      return 'Only admins can manage tags and lists.';
    default:
      if (error instanceof ApolloError && error.networkError) {
        return 'Could not reach the API. The change may not have been made: check before trying again.';
      }
      return 'The change could not be made.';
  }
};

/** A whole number with thousands separators. */
export const count = (value: number) => value.toLocaleString('en-US');

export const formatDay = (value: string | null | undefined) => (value ? dayjs(value).format('D MMM YYYY') : '');

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;

/**
 * How long before `today` a moment was: "Today", "3 days ago", "2 months ago", "4 years ago".
 * Empty for a moment after today (the demo seed has some), which has no honest "ago".
 */
export const sinceText = (value: string | null | undefined, today: string): string => {
  if (!value) return '';
  const then = dayjs(value);
  const now = dayjs(today).endOf('day');
  if (then.isAfter(now)) return '';
  const days = now.diff(then, 'day');
  if (days < 1) return 'Today';
  if (days < 30) return plural(days, 'day');
  const months = now.diff(then, 'month');
  if (months < 12) return plural(Math.max(1, months), 'month');
  return plural(now.diff(then, 'year'), 'year');
};

/** Not given to anyone in the year before `today` (or ever): the tag manager's "dead tags". */
export const isStale = (lastAddedAt: string | null | undefined, today: string) =>
  !lastAddedAt || dayjs(lastAddedAt).isBefore(dayjs(today).subtract(1, 'year'));
